/**
 *  [2021] Zettant Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Zettant Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Zettant Incorporated and its
 * suppliers and may be covered by Japan. and Foreign Patents,
 * patents in process, and are protected by trade secret or
 * copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless
 * prior written permission is obtained from Zettant Incorporated.
 */

import {CacheManage, getAllTables, prepareTable} from '@zettant/cacheManage';
import {
  createRecordProc, readRecordsByTime2Proc, deleteRecordProc, bulkCreateRecordProc, flushForTesting
} from './wrappers';
import pako from 'pako';
import {Base64} from 'js-base64';
import {sleep} from '@zettant/commonUtils/utils';


let tableName = '';
let myEmail = '';

const isPreferenceAvailable = (): boolean => !!tableName;  // EXP: preferenceTableが使える状態になっているかどうかはtableNameに名前が設定されているかどうかで判定できる


const preparePreferenceTable = async (tblName?: string, email?: string, removeLeadTime?: number, adapters?: any) => {
  if (tblName) tableName = tblName;
  if (email) myEmail = email;
  let table;
  if (adapters) {
    table = await prepareTable(tableName, 'ownerName', 'kind', email, removeLeadTime, {
      createAdapter: adapters.createAdapter,
      updateAdapter: adapters.createAdapter,
      bulkCreateAdapter: adapters.bulkCreateAdapter,
      bulkUpdateAdapter: adapters.bulkCreateAdapter,
      readAdapter2: adapters.readAdapter2,
      deleteAdapter: adapters.deleteAdapter,
      flushForTestingAdapter: adapters.flushForTestingAdapter,
    });
  } else {
    table = await prepareTable(tableName, 'ownerName', 'kind', email, removeLeadTime, {
      createAdapter: createRecordProc,
      updateAdapter: createRecordProc,
      bulkCreateAdapter: bulkCreateRecordProc,
      bulkUpdateAdapter: bulkCreateRecordProc,
      readAdapter2: readRecordsByTime2Proc,
      deleteAdapter: deleteRecordProc,
      flushForTestingAdapter: flushForTesting,
    });
  }
  if (removeLeadTime) table.setRemoveLeadTime(removeLeadTime);
};

const setEmail = (email: string) => myEmail = email;


const getPreferenceTable = async (): Promise<CacheManage> => {
  let counter = 100;
  do {
    console.log('tableName::::', tableName);
    const tbl = getAllTables()[tableName];
    console.log('tbl::::', tbl);
    if (tbl) return tbl;
    await sleep(50); // 50ms待ってみる
    counter -= 1;
  } while (counter > 0);
  if (typeof window !== 'undefined') alert('cacheManage (preference)を初期化できませんでした');
  return {} as CacheManage;
};


const putPreference = async (ownerName: string, kind: string, config: any) => {
  if (ownerName === '') ownerName = `u=${myEmail}`;
  const table = await getPreferenceTable();
  const compressed = pako.deflate(JSON.stringify(config));
  const conf = Base64.fromUint8Array(compressed);
  console.log('conf::',conf);
  console.log('table::',table);
  const ret = await table.put({ownerName, kind, config: conf});
};


const bulkPutPreferences = async (preferences: any[]) => {
  const table = await getPreferenceTable();
  const datToRegister: any = [];
  preferences.forEach((ent) => {
    if (ent.ownerName === '') ent.ownerName = `u=${myEmail}`;
    const compressed = pako.deflate(JSON.stringify(ent));
    const config = Base64.fromUint8Array(compressed);
    datToRegister.push({ownerName: ent.ownerName, kind: ent.kind, config});
  });
  await table.bulkPut(datToRegister);
};

const _getDecompressedData = (dat: any): any => {
  try {
    const decompressed = pako.inflate(Base64.toUint8Array(dat.config), {to: 'string'});
    dat.config = JSON.parse(decompressed);
    return dat;
  } catch (e) {
    return {};
  }
};

const getPreference = async (ownerName: string, kind: string): Promise<any> => {
  if (ownerName === '') ownerName = `u=${myEmail}`;
  const table = await getPreferenceTable();
  const allData = await table.getByTime(ownerName);
  const res: any = allData.find((e: any) => e.kind === kind);
  if (!res) return;
  _getDecompressedData(res);
  return res.config;
};

const getAllPreferences = async (ownerName: string): Promise<any> => {
  if (ownerName === '') ownerName = `u=${myEmail}`;
  const table = await getPreferenceTable();
  const res = await table.getByTime(ownerName);
  const promises: any = [];
  for (let i = 0; i < res.length; i++) {
    _getDecompressedData(res[i]);
    promises.push(res[i]);
  }
  return await Promise.all(promises);
};

const delPreferences = async (ownerName: string, kinds: string[]) => {
  if (ownerName === '') ownerName = `u=${myEmail}`;
  const table = await getPreferenceTable();
  const dat = [] as any[];
  for (const kind of kinds) {
    dat.push({ownerName, kind});
  }
  await table.bulkRemove(dat);
};


export {
  isPreferenceAvailable,
  preparePreferenceTable,
  getPreferenceTable,
  setEmail,
  getAllPreferences,
  putPreference,
  bulkPutPreferences,
  getPreference,
  delPreferences
};
